import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchLocations,
  fetchUBTList as fetchUBTListAPI,
} from "../../services/auth/ubtService";
import { DropdownOption } from "../../components/common/Dropdown";
import {
  transformToDropdownOptions,
  transformCostCenterOptions,
  addDefaultOption,
} from "../../utils/dropdownTransformers";
import { groupUBTItemsByTeam } from "../../utils/selectUBTDataTransformers";
import type {
  SelectUBTState,
  APIResponse,
  ApiMetaResponse,
  EditUBTSearchItem,
} from "../../types/selectUBT.types";
import endpoints from "../../api/endpoints";
import api from "../../api/axios";
import {
  downloadCsvFile,
  resolveCsvFileName,
  decompressGzipBlob,
} from "../../utility/CsvReportUtility";
import { getPstDateTimeParts } from "../../utils/pstDateTimeSuffix";

// Exported constants for reuse across components
export const defaultOption: DropdownOption = {
  id: 0,
  label: "All",
  value: "All",
};

export const defaultRegionOption: DropdownOption = {
  id: "",
  label: "Select",
  value: "Select",
};

export const searchByOptions = [
  { id: 0, label: "UBT Name", value: "UBTName" },
  { id: 1, label: "Cost Center Name", value: "CostCenterName" },
  { id: 2, label: "Cost Center Number", value: "CostCenterNumber" },
  { id: 3, label: "UBT ID#", value: "UBTId" },
];

/** The `searchBy` value that enables UBT ID# wildcard search and From/To ID range inputs. */
export const UBT_ID_SEARCH_BY_VALUE = "UBTId";

const initialState: SelectUBTState = {
    exportGLDataLoading: false,
    exportGLDataError: null,
    exportUBTDetailsLoading: false,
    exportUBTDetailsError: null,
    privilegeRolesLoading: false,
    privilegeRolesError: null,
  selectUBTList: [],
  editUBTList: [],
  loading: false,
  error: null,
  currentPage: 1,
  perPage: 20,
  totalItems: 0,
  totalPages: 0,
  teamsCount: 0,
  // Dropdown options - start empty, will be populated by API calls
  regionOptions: [],
  entityOptions: [],
  areaOptions: [],
  centerOptions: [],
  locationOptions: [],
  costCenterOptions: [defaultOption],
  // Selected codes tracking
  selectedRegionCode: defaultRegionOption,
  selectedEntityCode: defaultOption,
  selectedAreaCode: defaultOption,
  selectedCenterCode: defaultOption,
  selectedLocationCode: defaultOption,
  sortByCostCenter: "id",
  selectedCostCenterCode: { isAllSelected: true, selectedIds: [] },
  selectedSearchBy: searchByOptions[0], // Default to "UBT Name"
  selectedSearchByText: "",
  selectedFromId: "",
  selectedToId: "",
  // Consolidated loading state for all dropdown options
  loadingDropdowns: false,
  dropdownRequestsInFlight: 0,
  hasRunInitialSearch: false,
  searchParams: {},
  errors: {},
};

/**
 * Async thunk to export GL Data as CSV.
 * Fetches the GL data report from the API and triggers a browser download.
 * @param params - Query parameters for the export API (region, area, cost center, etc.)
 */
export const exportGLDataCSV = createAsyncThunk<
  void,
  Record<string, string>,
  { rejectValue: string }
>(
  "selectUBT/exportGLDataCSV",
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.ubt.reports.exportGLDataCSV, {
        params,
        responseType: "blob",
      });
      const fileName = resolveCsvFileName(
        response.headers["content-disposition"] as string | undefined,
        "UBT_GL_DATA.csv",
      );
      downloadCsvFile(response.data as Blob, fileName);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err?.response?.data?.message ?? err?.message ?? "Failed to export GL Data CSV",
      );
    }
  },
);

/**
 * Async thunk to export UBT Details by search criteria as CSV.
 * Uses the current search filters to build the request and triggers a browser download.
 * @param params - Query parameters matching the /ubt/reports/details/by-criteria API contract
 */
export const exportUBTDetailsByCriteriaCSV = createAsyncThunk<
  void,
  Record<string, string>,
  { rejectValue: string }
>(
  "selectUBT/exportUBTDetailsByCriteriaCSV",
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.ubt.reports.exportDetailsByCriteria,
        {
          params,
          headers: { accept: "text/csv" },
          responseType: "blob",
        },
      );
      const fileName = resolveCsvFileName(
        response.headers["content-disposition"] as string | undefined,
        "UBT_DETAILS.csv",
      );
      downloadCsvFile(response.data as Blob, fileName);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err?.response?.data?.message ?? err?.message ?? "Failed to export UBT Details CSV",
      );
    }
  },
);

/**
 * Downloads the privilege roles by-criteria report as CSV.
 * Accepts the active search filters from the Find and Select UBT page.
 */
type PrivilegeRolesCSVParams = {
  sortColumn?: string;
  sortOrder?: string;
  regionCode?: string;
  companyCd?: string;
  areaCode?: string;
  serviceAreaCode?: string;
  locationCode?: string;
  deptIds?: string;
  queryBy?: string;
  searchString?: string;
  fromId?: string;
  toId?: string;
};

/** Builds the non-empty query parameters for the privilege roles export. */
const buildPrivilegeRolesCSVParams = (
  values: PrivilegeRolesCSVParams,
): Record<string, string> =>
  Object.entries(values).reduce<Record<string, string>>(
    (params, [key, value]) => {
      if (value) params[key] = value;
      return params;
    },
    {},
  );

export const downloadPrivilegeRolesCSV = createAsyncThunk<
  void,
  PrivilegeRolesCSVParams & { nuid?: string | null },
  { rejectValue: string }
>(
  "selectUBT/downloadPrivilegeRolesCSV",
  async (
    {
      sortColumn,
      sortOrder,
      regionCode,
      companyCd,
      areaCode,
      serviceAreaCode,
      locationCode,
      deptIds,
      queryBy,
      searchString,
      fromId,
      toId,
      nuid,
    },
    { rejectWithValue },
  ) => {
    try {
      const params = buildPrivilegeRolesCSVParams({
        sortColumn,
        sortOrder,
        regionCode,
        companyCd,
        areaCode,
        serviceAreaCode,
        locationCode,
        deptIds,
        queryBy,
        searchString,
        fromId,
        toId,
      });

      const response = await api.get(
        endpoints.ubt.teamsReport.privilegeRolesByCriteria,
        {
          params,
          headers: {
            accept: "text/csv",
            ...(nuid ? { "x-nuid": nuid } : {}),
          },
          responseType: "blob",
        },
      );

      const { month, day, year, hour, minute, second } = getPstDateTimeParts();
      const fileName = `UBT_PRIVILEGE_MEMBERS_AND_ROLES_${month}-${day}-${year}-${hour}-${minute}-${second}.csv`;
      const decompressedBlob = await decompressGzipBlob(response.data as Blob);
      downloadCsvFile(decompressedBlob, fileName);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
      if (err?.response?.status === 429) {
        return rejectWithValue("RATE_LIMITED_429");
      }
      return rejectWithValue(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to download privilege roles CSV. Please try again.",
      );
    }
  },
);

export const fetchUBTList = createAsyncThunk(
  "selectUBT/fetchUBTList",
  async (
    {
      page,
      perPage,
      sortBy,
      sortOrder,
      searchBy,
      searchText,
      fromId,
      toId,
      regionCode,
      entityCode,
      areaCode,
      centerCode,
      locationCode,
      costCenterIds,
      countOnly = false,
    }: {
      page: number;
      perPage: number;
      sortBy?: string;
      sortOrder?: "ASC" | "DESC";
      searchBy?: string;
      searchText?: string;
      fromId?: string;
      toId?: string;
      regionCode?: string;
      entityCode?: string;
      areaCode?: string;
      centerCode?: string;
      locationCode?: string;
      costCenterIds?: (string | number)[] | string; // Accept both "All" string and array
      countOnly?: boolean;
      /** UI-only flag (never sent to the API) selecting how the fulfilled reducer stores results. */
      resultMode?: "select" | "edit";
    },
    { rejectWithValue },
  ) => {
    try {
      // Normalize costCenterIds to array format expected by API
      let normalizedCostCenterIds: (string | number)[] | undefined;
      if (costCenterIds) {
        normalizedCostCenterIds =
          typeof costCenterIds === "string" ? [costCenterIds] : costCenterIds;
      }
      const params = {
        page,
        perPage,
        sortBy,
        sortOrder,
        searchBy,
        searchText,
        fromId,
        toId,
        regionCode,
        entityCode,
        areaCode,
        centerCode,
        locationCode,
        costCenterIds: normalizedCostCenterIds,
      };

      let response, count;

      if (countOnly) {
        [response, count] = await Promise.all([
          fetchUBTListAPI({ ...params, countOnly: false }),
          fetchUBTListAPI({ ...params, countOnly: true }),
        ]);
      } else {
        response = await fetchUBTListAPI({ ...params, countOnly: false });
      }

      return { response, count };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch UBT list";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch Region options
export const fetchRegionOptions = createAsyncThunk(
  "selectUBT/fetchRegionOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "REGION",
      });

      const regionOptions = transformToDropdownOptions(
        response?.selectedList ?? [],
        false,
      );
      return regionOptions;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch region options";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch Entity options based on region (returned with AREA call)
export const fetchEntityOptions = createAsyncThunk(
  "selectUBT/fetchEntityOptions",
  async (regionCode: string, { rejectWithValue }) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "AREA",
        regionCode,
      });

      const entityOptions = addDefaultOption(
        transformToDropdownOptions(response?.entityList ?? [], false),
        defaultOption,
      );
      return entityOptions;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch entity options";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch Area options based on region
export const fetchAreaOptions = createAsyncThunk(
  "selectUBT/fetchAreaOptions",
  async (
    {
      regionCode,
      sortBy = "DEPTID",
      isRestored = false,
    }: { regionCode: string; sortBy?: string; isRestored?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "AREA",
        regionCode,
        sortBy: sortBy === "name" ? "DEPTNAME" : "DEPTID",
      });

      const areaOptions = addDefaultOption(
        transformToDropdownOptions(response?.selectedList ?? []),
        defaultOption,
      );

      const entityOptions = addDefaultOption(
        transformToDropdownOptions(response?.entityList ?? []),
        defaultOption,
      );

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { areaOptions, entityOptions, costCenterOptions, isRestored };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch area options";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch Center options based on area
export const fetchCenterOptions = createAsyncThunk(
  "selectUBT/fetchCenterOptions",
  async (
    {
      regionCode,
      areaCode,
      sortBy = "DEPTID",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "CENTER",
        regionCode,
        areaCode,
        sortBy: sortBy === "name" ? "DEPTNAME" : "DEPTID",
      });

      const centerOptions = addDefaultOption(
        transformToDropdownOptions(response?.selectedList ?? []),
        defaultOption,
      );

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { centerOptions, costCenterOptions, isRestored };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch center options";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch Location options based on center
export const fetchLocationOptions = createAsyncThunk(
  "selectUBT/fetchLocationOptions",
  async (
    {
      regionCode,
      areaCode,
      centerCode,
      sortBy = "DEPTID",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      centerCode: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "LOCATION",
        regionCode,
        areaCode,
        serviceAreaCode: centerCode,
        sortBy: sortBy === "name" ? "DEPTNAME" : "DEPTID",
      });
      let locationOptions: DropdownOption[] = [];
      if (centerCode !== defaultOption.value) {
        locationOptions = addDefaultOption(
          transformToDropdownOptions(response?.selectedList ?? []),
          defaultOption,
        );
      }

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { locationOptions, costCenterOptions, isRestored };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch location options";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch Cost Center options based on center and sort criteria
export const fetchCostCenterOptions = createAsyncThunk(
  "selectUBT/fetchCostCenterOptions",
  async (
    {
      regionCode,
      areaCode,
      centerCode,
      locationCode,
      sortBy = "DEPTID",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      centerCode: string;
      locationCode?: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "COST_CENTER",
        regionCode,
        areaCode,
        serviceAreaCode: centerCode,
        locationCode: locationCode ?? "ALL",
        sortBy: sortBy === "name" ? "DEPTNAME" : "DEPTID",
      });

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { costCenterOptions, isRestored };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch cost center options";
      return rejectWithValue(errorMessage);
    }
  },
);

const selectUBTSlice = createSlice({
  name: "selectUBT",
  initialState,
  reducers: {
    resetSelectUBTState: (state) => {
      state.selectUBTList = [];
      state.loading = false;
      state.loadingDropdowns = false;
      state.dropdownRequestsInFlight = 0;
      state.error = null;
      state.currentPage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
      state.teamsCount = 0;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setSelectedRegionCode: (state, action) => {
      state.selectedRegionCode = action.payload ?? defaultRegionOption;
    },
    setSelectedEntityCode: (state, action) => {
      state.selectedEntityCode = action.payload ?? defaultOption;
    },
    setSelectedAreaCode: (state, action) => {
      state.selectedAreaCode = action.payload ?? defaultOption;
    },
    setSelectedCenterCode: (state, action) => {
      state.selectedCenterCode = action.payload ?? defaultOption;
    },
    setSelectedLocationCode: (state, action) => {
      state.selectedLocationCode = action.payload ?? defaultOption;
    },
    setSelectedSearchBy: (state, action) => {
      state.selectedSearchBy = action.payload ?? searchByOptions[0];
    },
    setSelectedSearchByText: (state, action) => {
      state.selectedSearchByText = action.payload ?? "";
    },
    setSelectedFromId: (state, action) => {
      state.selectedFromId = action.payload ?? "";
    },
    setSelectedToId: (state, action) => {
      state.selectedToId = action.payload ?? "";
    },
    setSelectedCostCenterCode: (state, action) => {
      state.selectedCostCenterCode = action.payload ?? {
        isAllSelected: true,
        selectedIds: [],
      };
    },
    setHasRunInitialSearch: (state, action) => {
      state.hasRunInitialSearch = action.payload;
    },
    setSearchParams: (state, action) => {
      state.searchParams = action.payload;
    },
    setSortByCostCenter: (state, action) => {
      state.sortByCostCenter = action.payload;
    },
    restoreSearchFilters: (state, action) => {
      const {
        selectedRegionCode,
        selectedEntityCode,
        selectedAreaCode,
        selectedCenterCode,
        selectedLocationCode,
        selectedCostCenterCode,
        sortByCostCenter,
        selectedSearchBy,
        selectedSearchByText,
        selectedFromId,
        selectedToId,
      } = action.payload;

      if (selectedRegionCode) state.selectedRegionCode = selectedRegionCode;
      if (selectedEntityCode) state.selectedEntityCode = selectedEntityCode;
      if (selectedAreaCode) state.selectedAreaCode = selectedAreaCode;
      if (selectedCenterCode) state.selectedCenterCode = selectedCenterCode;
      if (selectedCostCenterCode)
        state.selectedCostCenterCode = selectedCostCenterCode;
      if (selectedLocationCode)
        state.selectedLocationCode = selectedLocationCode;
      if (sortByCostCenter) state.sortByCostCenter = sortByCostCenter;
      if (selectedSearchBy) state.selectedSearchBy = selectedSearchBy;
      if (selectedSearchByText)
        state.selectedSearchByText = selectedSearchByText;
      if (selectedFromId !== undefined) state.selectedFromId = selectedFromId;
      if (selectedToId !== undefined) state.selectedToId = selectedToId;
    },
    resetDependentDropdowns: (state, action) => {
      const { level } = action.payload;
      // Reset dropdowns based on the level that changed
      if (level === "region") {
        state.entityOptions = [];
        state.areaOptions = [];
        state.centerOptions = [];
        state.locationOptions = [];
        state.selectedEntityCode = defaultOption;
        state.selectedAreaCode = defaultOption;
        state.selectedCenterCode = defaultOption;
        state.selectedLocationCode = defaultOption;
        state.costCenterOptions = [defaultOption];
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
        state.sortByCostCenter = "id"; // Reset sorting to default when region changes
      } else if (level === "area") {
        state.centerOptions = [];
        state.locationOptions = [];
        state.selectedLocationCode = defaultOption;
        state.selectedCenterCode = defaultOption;
        state.costCenterOptions = [defaultOption];
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
        state.sortByCostCenter = "id";
      } else if (level === "center") {
        state.locationOptions = [];
        state.selectedLocationCode = defaultOption;
        state.costCenterOptions = [defaultOption];
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
        state.sortByCostCenter = "id";
      } else if (level === "location") {
        state.costCenterOptions = [defaultOption];
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      }
    },
  },
  extraReducers: (builder) => {
        // Export GL Data CSV
        builder.addCase(exportGLDataCSV.pending, (state) => {
          state.exportGLDataLoading = true;
          state.exportGLDataError = null;
        });
        builder.addCase(exportGLDataCSV.fulfilled, (state) => {
          state.exportGLDataLoading = false;
          state.exportGLDataError = null;
        });
        builder.addCase(exportGLDataCSV.rejected, (state, action) => {
          state.exportGLDataLoading = false;
          state.exportGLDataError = action.payload || "Failed to export GL Data CSV";
        });
    // Export UBT Details by Criteria CSV
    builder.addCase(exportUBTDetailsByCriteriaCSV.pending, (state) => {
      state.exportUBTDetailsLoading = true;
      state.exportUBTDetailsError = null;
    });
    builder.addCase(exportUBTDetailsByCriteriaCSV.fulfilled, (state) => {
      state.exportUBTDetailsLoading = false;
      state.exportUBTDetailsError = null;
    });
    builder.addCase(exportUBTDetailsByCriteriaCSV.rejected, (state, action) => {
      state.exportUBTDetailsLoading = false;
      state.exportUBTDetailsError = action.payload ?? "Failed to export UBT Details CSV";
    });
    // Download Privilege Roles CSV
    builder.addCase(downloadPrivilegeRolesCSV.pending, (state) => {
      state.privilegeRolesLoading = true;
      state.privilegeRolesError = null;
    });
    builder.addCase(downloadPrivilegeRolesCSV.fulfilled, (state) => {
      state.privilegeRolesLoading = false;
      state.privilegeRolesError = null;
    });
    builder.addCase(downloadPrivilegeRolesCSV.rejected, (state, action) => {
      state.privilegeRolesLoading = false;
      state.privilegeRolesError =
        action.payload ??
        action.error.message ??
        "Failed to download privilege roles CSV";
    });
    // UBT List
    builder.addCase(fetchUBTList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUBTList.fulfilled, (state, action) => {
      state.loading = false;
      const payload: ApiMetaResponse = action.payload?.response ?? {};
      const count: number = action.payload?.count?.count ?? 0;
      const resultMode = action.meta.arg.resultMode ?? "select";

      if (payload?.meta && payload?.items && Array.isArray(payload.items)) {
        if (resultMode === "edit") {
          state.editUBTList = payload.items as unknown as EditUBTSearchItem[];
        } else {
          state.selectUBTList = groupUBTItemsByTeam(payload.items);
        }
        state.totalItems = payload.meta.total ?? 0;
        state.totalPages = payload.meta.totalPages ?? 1;
        state.currentPage = payload.meta.pageNumber ?? 1;
        state.perPage = payload.meta.pageSize ?? 20;
        state.teamsCount = count > 0 ? count : state.teamsCount; // Update teams count only if count is returned from API
      } else {
        if (resultMode === "edit") {
          state.editUBTList = [];
        } else {
          state.selectUBTList = [];
        }
        state.totalItems = 0;
        state.totalPages = 0;
        state.teamsCount = 0;
      }
    });
    builder.addCase(fetchUBTList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Region Options
    builder.addCase(fetchRegionOptions.pending, (state) => {
      state.dropdownRequestsInFlight = (state.dropdownRequestsInFlight ?? 0) + 1;
      state.loadingDropdowns = true;
      state.errors.region = undefined;
    });
    builder.addCase(fetchRegionOptions.fulfilled, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.regionOptions = action.payload ?? [];
      state.errors.region = undefined;
    });
    builder.addCase(fetchRegionOptions.rejected, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.errors.region = action.payload as string;
    });

    // Entity Options
    builder.addCase(fetchEntityOptions.pending, (state) => {
      state.dropdownRequestsInFlight = (state.dropdownRequestsInFlight ?? 0) + 1;
      state.loadingDropdowns = true;
      state.errors.entity = undefined;
    });
    builder.addCase(fetchEntityOptions.fulfilled, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.entityOptions = action.payload ?? [];
      state.errors.entity = undefined;
    });
    builder.addCase(fetchEntityOptions.rejected, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.errors.entity = action.payload as string;
    });

    // Area Options
    builder.addCase(fetchAreaOptions.pending, (state) => {
      state.dropdownRequestsInFlight = (state.dropdownRequestsInFlight ?? 0) + 1;
      state.loadingDropdowns = true;
      state.errors.area = undefined;
    });
    builder.addCase(fetchAreaOptions.fulfilled, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      const { areaOptions, entityOptions, costCenterOptions } =
        action.payload ?? {
          areaOptions: [],
          entityOptions: [],
          costCenterOptions: [],
        };
      state.areaOptions = areaOptions;
      state.entityOptions = entityOptions;
      state.costCenterOptions = costCenterOptions ?? [];
      state.errors.area = undefined;
      if (!action.payload?.isRestored) {
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
        state.sortByCostCenter = "id";
      }
    });
    builder.addCase(fetchAreaOptions.rejected, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.errors.area = action.payload as string;
    });

    // Center Options
    builder.addCase(fetchCenterOptions.pending, (state) => {
      state.dropdownRequestsInFlight = (state.dropdownRequestsInFlight ?? 0) + 1;
      state.loadingDropdowns = true;
      state.errors.center = undefined;
    });
    builder.addCase(fetchCenterOptions.fulfilled, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.centerOptions = action.payload?.centerOptions ?? [];
      state.costCenterOptions = action.payload?.costCenterOptions ?? [];
      state.errors.center = undefined;
      if (!action.payload?.isRestored) {
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
        state.sortByCostCenter = "id"; // Reset sorting to default when center changes
      }
    });
    builder.addCase(fetchCenterOptions.rejected, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.errors.center = action.payload as string;
    });

    // Location Options
    builder.addCase(fetchLocationOptions.pending, (state) => {
      state.dropdownRequestsInFlight = (state.dropdownRequestsInFlight ?? 0) + 1;
      state.loadingDropdowns = true;
      state.errors.location = undefined;
    });
    builder.addCase(fetchLocationOptions.fulfilled, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.locationOptions = action.payload?.locationOptions ?? [];
      state.costCenterOptions = action.payload?.costCenterOptions ?? [];
      state.errors.location = undefined;
      if (!action.payload?.isRestored) {
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
        state.sortByCostCenter = "id";
      }
    });
    builder.addCase(fetchLocationOptions.rejected, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.errors.location = action.payload as string;
    });

    // Cost Center Options
    builder.addCase(fetchCostCenterOptions.pending, (state) => {
      state.dropdownRequestsInFlight = (state.dropdownRequestsInFlight ?? 0) + 1;
      state.loadingDropdowns = true;
      state.errors.costCenter = undefined;
    });
    builder.addCase(fetchCostCenterOptions.fulfilled, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.costCenterOptions = action.payload?.costCenterOptions ?? [];
      state.errors.costCenter = undefined;
      if (!action.payload?.isRestored) {
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      }
    });
    builder.addCase(fetchCostCenterOptions.rejected, (state, action) => {
      state.dropdownRequestsInFlight = Math.max((state.dropdownRequestsInFlight ?? 1) - 1, 0);
      state.loadingDropdowns = state.dropdownRequestsInFlight > 0;
      state.errors.costCenter = action.payload as string;
    });
  },
});

export const {
  resetSelectUBTState,
  setCurrentPage,
  resetDependentDropdowns,
  setSelectedRegionCode,
  setSelectedEntityCode,
  setSelectedAreaCode,
  setSelectedCenterCode,
  setSelectedLocationCode,
  setSelectedCostCenterCode,
  setHasRunInitialSearch,
  setSearchParams,
  setSortByCostCenter,
  restoreSearchFilters,
  setSelectedSearchBy,
  setSelectedSearchByText,
  setSelectedFromId,
  setSelectedToId,
} = selectUBTSlice.actions;

// Selectors
export const selectUBTState = (state: any) => state.selectUBTSlice;

type SelectUBTSelectorState = { selectUBTSlice: SelectUBTState };

/** Selects whether the privilege roles CSV download is in progress. */
export const selectPrivilegeRolesLoading = (state: SelectUBTSelectorState): boolean =>
  state.selectUBTSlice.privilegeRolesLoading;

/** Selects the privilege roles CSV download error message, or null if none. */
export const selectPrivilegeRolesError = (state: SelectUBTSelectorState): string | null =>
  state.selectUBTSlice.privilegeRolesError;

export default selectUBTSlice.reducer;
